/**
 * TanStack Query Provider Component
 *
 * This component wraps the application with QueryClientProvider and ReactQueryDevtools.
 * It provides the TanStack Query context to all child components and enables
 * the development tools for debugging and monitoring queries.
 *
 * Features:
 * - QueryClient provider setup
 * - Development tools integration
 * - Error boundary integration (optional)
 * - Persistent cache (optional)
 * - Network status monitoring
 */

import { QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import React from 'react';
import { queryClient } from '../config/queryClient';

/**
 * QueryProvider Component
 *
 * This component should wrap your entire application or the parts that need
 * access to TanStack Query functionality.
 *
 * @param {Object} props - Component props
 * @param {React.ReactNode} props.children - Child components
 * @param {boolean} props.enableDevtools - Whether to enable React Query DevTools (default: true in development)
 * @param {Object} props.devtoolsConfig - Configuration for React Query DevTools
 */
const QueryProvider = ({ children, enableDevtools = process.env.NODE_ENV === 'development' || true, devtoolsConfig = {} }) => {
    /**
     * Default DevTools Configuration
     *
     * These settings control the behavior and appearance of the React Query DevTools.
     */
    const defaultDevtoolsConfig = {
        // Initial open state of the devtools
        initialIsOpen: false,

        // // Error boundary for devtools
        errorBoundaryFallback: ({ error, resetErrorBoundary }) => (
            <div style={{ padding: '20px', color: 'red' }}>
                <h3>DevTools Error</h3>
                <pre>{error.message}</pre>
                <button onClick={resetErrorBoundary}>Reset</button>
            </div>
        ),
    };

    return (
        <QueryClientProvider client={queryClient}>
            {children}

            {/* React Query DevTools - Only in development or when explicitly enabled */}
            {enableDevtools && <ReactQueryDevtools initialIsOpen={false} {...defaultDevtoolsConfig} />}
        </QueryClientProvider>
    );
};

/**
 * QueryErrorBoundary Component (Optional)
 *
 * This component can be used to catch and handle errors that occur
 * within the Query provider context.
 */
export class QueryErrorBoundary extends React.Component {
    constructor(props) {
        super(props);
        this.state = { hasError: false, error: null };
    }

    static getDerivedStateFromError(error) {
        // Update state so the next render will show the fallback UI
        return { hasError: true, error };
    }

    componentDidCatch(error, errorInfo) {
        // Log error to console or error reporting service
        console.error('Query Error Boundary:', error, errorInfo);

        // You can also log the error to an error reporting service here
        // Example: Sentry.captureException(error, { extra: errorInfo });
    }

    render() {
        if (this.state.hasError) {
            // Render custom fallback UI
            return (
                <div
                    style={{
                        padding: '20px',
                        textAlign: 'center',
                        backgroundColor: '#f8f9fa',
                        border: '1px solid #dee2e6',
                        borderRadius: '8px',
                        margin: '20px',
                    }}
                >
                    <h2 style={{ color: '#dc3545', marginBottom: '16px' }}>Something went wrong with data loading</h2>
                    <p style={{ color: '#6c757d', marginBottom: '16px' }}>{this.state.error?.message || 'An unexpected error occurred'}</p>
                    <button
                        onClick={() => {
                            this.setState({ hasError: false, error: null });
                            queryClient.clear(); // Clear the query cache
                            window.location.reload(); // Reload the page
                        }}
                        style={{
                            backgroundColor: '#007bff',
                            color: 'white',
                            border: 'none',
                            padding: '8px 16px',
                            borderRadius: '4px',
                            cursor: 'pointer',
                        }}
                    >
                        Reload Application
                    </button>
                </div>
            );
        }

        return this.props.children;
    }
}

/**
 * Higher-Order Component for Query Error Boundary
 *
 * This HOC wraps components with the QueryErrorBoundary for easier usage.
 */
export const withQueryErrorBoundary = (Component) => {
    const WrappedComponent = (props) => (
        <QueryErrorBoundary>
            <Component {...props} />
        </QueryErrorBoundary>
    );

    WrappedComponent.displayName = `withQueryErrorBoundary(${Component.displayName || Component.name})`;

    return WrappedComponent;
};

/**
 * Network Status Hook
 *
 * This hook provides network status information that can be used
 * to show offline indicators or handle offline scenarios.
 */
export const useNetworkStatus = () => {
    const [isOnline, setIsOnline] = React.useState(navigator.onLine);

    React.useEffect(() => {
        const handleOnline = () => setIsOnline(true);
        const handleOffline = () => setIsOnline(false);

        window.addEventListener('online', handleOnline);
        window.addEventListener('offline', handleOffline);

        return () => {
            window.removeEventListener('online', handleOnline);
            window.removeEventListener('offline', handleOffline);
        };
    }, []);

    return { isOnline, isOffline: !isOnline };
};

/**
 * Query Cache Status Hook
 *
 * This hook provides information about the current state of the query cache.
 * Useful for debugging and monitoring in development.
 */
export const useQueryCacheStatus = () => {
    const [cacheStats, setCacheStats] = React.useState(null);

    React.useEffect(() => {
        const updateStats = () => {
            const queryCache = queryClient.getQueryCache();
            const queries = queryCache.getAll();

            setCacheStats({
                totalQueries: queries.length,
                staleQueries: queries.filter((query) => query.isStale()).length,
                fetchingQueries: queries.filter((query) => query.state.isFetching).length,
                errorQueries: queries.filter((query) => query.state.isError).length,
                successQueries: queries.filter((query) => query.state.isSuccess).length,
            });
        };

        // Update stats initially
        updateStats();

        // Update stats periodically (only in development)
        let interval;
        if (process.env.NODE_ENV === 'development') {
            interval = setInterval(updateStats, 5000); // Every 5 seconds
        }

        return () => {
            if (interval) {
                clearInterval(interval);
            }
        };
    }, []);

    return cacheStats;
};

export default QueryProvider;
